import React, { useCallback, useMemo, useRef, useState } from "react"
import ReportColumnSelector from "components/Common/ReportColumnSelector"
import { Button, Col, Form, Input, Label, Row } from "reactstrap"

import "flatpickr/dist/themes/material_blue.css"
import Flatpickr from "react-flatpickr"
import moment from "moment"
import { useSelector, useDispatch } from "react-redux"
import { isObject } from "lodash"
import Select from "react-select"
import { getProviderItems } from "store/provider-dashboard/provider-dashboard-action"
import {
  getProviderConsumptionsFacilities,
  getProviderConsumptionsIPs,
  getProviderConsumptionsProvince,
} from "store/provider-consumption/province-consumption-action"
import { providerReportsAction } from "store/provider-reports/p-report-slice"
import { providerConsumptionAction } from "store/provider-consumption/province-consumption-slice"
import { QUARTERS } from "constants/data"
import getDashboardSelectProps from "utils/dashboardFilterStyles"

const FILTER_DEBOUNCE_MS = 400

const ProviderConsumptionReporFilter = ({
  onAdvancedSearchSubmit,
  loading,
  error,
  columns = [],
  onToggleColumn = () => {},
  onReorder = () => {},
}) => {
  const dateFlatpickerfrom = useRef(null)
  const dateFlatpickerto = useRef(null)
  const dispatchTimerRef = useRef(null)
  const [category, setCategory] = useState(null)
  const [item, setItem] = useState(null)
  const [batch_number, setBatchNumber] = useState("")
  const [project, setProject] = useState(null)
  const [province, setProvince] = useState(null)
  const [facility, setFacility] = useState(null)
  const [ip, setIP] = useState(null)
  const [qtr, setQtr] = useState([])

  const dispatch = useDispatch()

  const [date, setDate] = useState({
    from: null,
    to: null,
  })

  // Retrieve categories data from Redux store
  const categories = useSelector(
    state => state.providerDashboard.ProviderItemCategoires
  )

  // Retrieve projects data from Redux store
  const projects = useSelector(
    state => state.providerConsumptions.providerConsumpProjects
  )

  // Retrieve ips data from Redux store
  const ips = useSelector(
    state => state.providerConsumptions.providerConsumpIPs
  )

  // Retrieve items data from Redux store
  const items = useSelector(state => state.providerDashboard.ProviderItems)

  // Retrieve provinces data from Redux store
  const provinces = useSelector(
    state => state.providerConsumptions.providerConsumpProvinces
  )

  // Retrieve facilities data from Redux store
  const facilities = useSelector(
    state => state.providerConsumptions.providerConsumpFacilities
  )

  // get filtered items based select box props needs
  const filteredItems = useMemo(
    () =>
      items?.data.map(item => ({
        value: item.id,
        label: `${item.unfp_item_name} ( ${item.item_name} )`,
      })),
    [items]
  )

  // Build search params from current state
  const buildSearchParams = useCallback(() => {
    const data = {}
    data.date = {
      from: date.from ? date.from : null,
      to: date.to ? date.to : null,
    }
    if (item) {
      data.item = item.map(ite => (ite.value ? ite.value : ite))
    }
    if (qtr) {
      data.qtr = qtr.map(qt => (qt.value ? qt.value : qt))
    }
    data.category = isObject(category) ? category.value : null
    data.project_id = isObject(project) ? project.value : null
    data.ip_id = isObject(ip) ? ip.value : null
    data.province = isObject(province) ? province.value : null
    data.facility = isObject(facility) ? facility.value : null
    data.batch_number = batch_number
    return data
  }, [date, item, qtr, category, project, ip, province, facility, batch_number])

  const triggerSearch = useCallback((data) => {
    onAdvancedSearchSubmit(data)
  }, [onAdvancedSearchSubmit])

  const debouncedSearch = useCallback(() => {
    clearTimeout(dispatchTimerRef.current)
    dispatchTimerRef.current = setTimeout(() => {
      const data = buildSearchParams()
      triggerSearch(data)
    }, FILTER_DEBOUNCE_MS)
  }, [buildSearchParams, triggerSearch])

  // Store filtered data and dispatch the action using onAdvancedSearchSubmit callback function
  const handleSubmit = () => {
    clearTimeout(dispatchTimerRef.current)
    const data = buildSearchParams()
    triggerSearch(data)
  }

  // clear search when clear button clicked
  const onClearHandler = () => {
    clearTimeout(dispatchTimerRef.current)
    dateFlatpickerfrom.current.flatpickr.clear()
    dateFlatpickerto.current.flatpickr.clear()
    setItem(null)
    setCategory(null)
    setBatchNumber("")
    setDate({ from: null, to: null })
    setProject(null)
    setProvince(null)
    setFacility(null)
    setIP(null)
    setQtr([])
    dispatch(providerConsumptionAction.resetState())
    onAdvancedSearchSubmit({})
  }

  // store Item Categories and fetch items based item category
  const onCategoryChangeHandler = cate => {
    setCategory(cate)
    dispatch(getProviderItems(cate.value))
    debouncedSearch()
  }

  // store multiple items based selected
  const onItemChangeHandler = newItem => {
    const check = newItem.some(ite => ite.value === "all")
    if (check) {
      setItem(filteredItems)
    } else {
      setItem(newItem)
    }
    debouncedSearch()
  }

  // store multiple qtr value
  const onQtrChangeHandler = value => {
    const check = value.some(val => val.value === "all")
    if (check) {
      setQtr(QUARTERS)
    } else {
      setQtr(value)
    }
    debouncedSearch()
  }

  // store project and fetch consumption IPs based project id
  const onProjectChangeHandler = proj => {
    dispatch(getProviderConsumptionsIPs({ project_id: proj.value }))
    setProject(proj)
    debouncedSearch()
  }

  // store ips value and fetch province based ip and project id
  const onIPChangeHandler = ips => {
    setIP(ips)
    dispatch(
      getProviderConsumptionsProvince({
        ip_id: ips.value,
        project_id: project.value,
      })
    )
    debouncedSearch()
  }

  // store province and fetch facilites based ip id, project id, and province id
  const onProvinceChangeHandler = prov => {
    setProvince(prov)
    setFacility("")
    dispatch(
      getProviderConsumptionsFacilities({
        ip_id: ip?.value,
        province_id: prov.value,
        project_id: project.value,
      })
    )
    debouncedSearch()
  }

  // store facility value
  const onFacilityChangeHandler = value => {
    setFacility(value)
    debouncedSearch()
  }

  // store batch number value
  const onBatchNumberChange = evt => {
    setBatchNumber(evt.target.value)
    debouncedSearch()
  }

  return (
    <Form
      onSubmit={e => {
        e.preventDefault()
        handleSubmit()
        return false
      }}
    >
      <div className="mb-3">
        <Row>
          <Col>
            <Label className="form-label">Projects</Label>
            {projects?.data && (
              <Select
                options={projects?.data}
                value={project}
                onChange={onProjectChangeHandler}
                {...getDashboardSelectProps()}
              />
            )}
            {error?.errors?.project?.[0] && (
              <p className="text-danger">{error?.errors?.project?.[0]}</p>
            )}
          </Col>
          <Col>
            <Label className="form-label">IPs</Label>
            {ips?.data && (
              <Select
                options={ips?.data}
                value={ip}
                onChange={onIPChangeHandler}
                {...getDashboardSelectProps()}
              />
            )}
            {error?.errors?.ip_id?.[0] && (
              <p className="text-danger">{error?.errors?.ip_id?.[0]}</p>
            )}
          </Col>
        </Row>
      </div>
      <div className="mb-3">
        <Row>
          <Col>
            <Label className="form-label">Provinces</Label>
            {provinces?.data && (
              <Select
                options={provinces?.data}
                value={province}
                onChange={onProvinceChangeHandler}
                {...getDashboardSelectProps()}
              />
            )}
            {error?.errors?.province?.[0] && (
              <p className="text-danger">{error?.errors?.province?.[0]}</p>
            )}
          </Col>
          <Col>
            <Label className="form-label">Facility</Label>
            {facilities?.data && (
              <Select
                options={facilities?.data}
                value={facility}
                onChange={onFacilityChangeHandler}
                {...getDashboardSelectProps()}
              />
            )}
            {error?.errors?.facility?.[0] && (
              <p className="text-danger">{error?.errors?.facility?.[0]}</p>
            )}
          </Col>
        </Row>
      </div>

      <div className="mb-3">
        <Row>
          <Col>
            <Label className="form-label">Item Categories</Label>
            {categories?.data && (
              <Select
                options={categories?.data}
                value={category}
                onChange={onCategoryChangeHandler}
                {...getDashboardSelectProps()}
              />
            )}
            {error?.errors?.category?.[0] && (
              <p className="text-danger">{error?.errors?.category?.[0]}</p>
            )}
          </Col>
          <Col>
            <Label className="form-label">Items</Label>
            {filteredItems && (
              <Select
                isMulti={true}
                options={[{ value: "all", label: "All" }, ...filteredItems]}
                value={item}
                onChange={onItemChangeHandler}
                {...getDashboardSelectProps()}
              />
            )}
            {error?.errors?.item?.[0] && (
              <p className="text-danger">{error?.errors?.item?.[0]}</p>
            )}
          </Col>
        </Row>
      </div>

      <div className="mb-3">
        <Row>
          <Col>
            <Label className="form-label">Batch Number</Label>
            <Input
              name="batch_number"
              placeholder="Batch Number"
              type="text"
              onChange={onBatchNumberChange}
              value={batch_number}
            />
          </Col>
          <Col>
            <Label className="form-label">Quarter</Label>
            <Select
              isMulti={true}
              options={[{ value: "all", label: "All" }, ...QUARTERS]}
              value={qtr}
              onChange={onQtrChangeHandler}
              {...getDashboardSelectProps()}
            />
          </Col>
        </Row>
      </div>

      <div className="mb-3 ">
        <Row>
          <Col>
            <Label className="form-label">From Date</Label>
            <Flatpickr
              ref={dateFlatpickerfrom}
              className="form-control d-block"
              placeholder="dd M,yyyy"
              options={{
                altInput: true,
                altFormat: "Y-m-d",
                dateFormat: "Y-m-d",
              }}
              onChange={value => {
                setDate(prev => {
                  return {
                    ...prev,
                    from: moment(value[0]).format("YYYY-MM-DD"),
                  }
                })
                debouncedSearch()
              }}
            />
          </Col>
          <Col>
            <Label className="form-label">To Date</Label>
            <Flatpickr
              ref={dateFlatpickerto}
              className="form-control d-block"
              placeholder="dd M,yyyy"
              options={{
                altInput: true,
                altFormat: "Y-m-d",
                dateFormat: "Y-m-d",
              }}
              onChange={value => {
                setDate(prev => {
                  return { ...prev, to: moment(value[0]).format("YYYY-MM-DD") }
                })
                debouncedSearch()
              }}
            />
          </Col>
        </Row>
      </div>
      <ReportColumnSelector columns={columns} onToggle={onToggleColumn} onReorder={onReorder} />


      <div className="d-flex flex-wrap gap-2 mb-4">
        <Button color="primary" type="submit" disabled={loading}>
          {loading ? "Please Wait..." : "Apply Filters"}
        </Button>
        <Button color="danger" type="button" outline onClick={onClearHandler}>
          Reset All
        </Button>
      </div>
    </Form>
  )
}

export default ProviderConsumptionReporFilter
